/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import React, { useState } from 'react';
import { LinkButton, Text } from '@blend/enterprise-components';
import { useDispatch } from 'react-redux';
import { AxiosError } from 'axios';
import { NotificationItem } from '../../../components/common/Notifications/NotificationItem';
import { notaryAPI } from '../../../api/notary';
import { NotaryAPI } from '../../../api/notary/types';
import { addNotifications } from '../../../actions/pageActions';
import { usePermissions } from '../../../hooks/usePermissions';
import { RoomStatus } from '../../../shared/constants/room';

/**
 * Certain self-assigment errors should be propagated to the user, since the user can take action to fix the error.
 * This is a mapping of the error message we receive from the backend to the error message we want to display to the user.
 */
// TODO (RON-1066): Remove this once we have an error interface for explicitly handling certain errors
const actionableAssignmentErrors: { [key: string]: string } = {
  ['Notary must fill out their name and address in Notary Workspace before being assigned to a room.']: `Please update your profile information before assigning yourself to a room.`,
  ['Notary must have an active digital certificate before being assigned to a room.']: `Please upload a valid digital certificate before assigning yourself to a room.`,
};

const genericAssignmentErrorMessage = 'That action could not be completed. Try again.';

export namespace ActionCell {
  export interface Props {
    value: {
      roomId: string;
      notaryName: string | null;
      roomStatus: string;
    };
    fetchRooms: () => Promise<void>;
  }
}

export const ActionCell: React.FC<ActionCell.Props> = ({ value, fetchRooms }) => {
  const dispatch = useDispatch();
  const permissions = usePermissions();
  const [isSubmitting, setIsSubmitting] = useState(false);
  if (!!value.notaryName) {
    return <Text>{value.notaryName}</Text>;
  }

  const onAssignClick = async (e: React.MouseEvent) => {
    try {
      e.stopPropagation();
      setIsSubmitting(true);
      await notaryAPI.rooms.assignRoom(value.roomId);
      await fetchRooms();
      setIsSubmitting(false);
    } catch (e) {
      let displayErrMessage = genericAssignmentErrorMessage;
      let shouldAutoRemove = true;
      const err = e as AxiosError<{ message?: string }>;
      if (err.response?.data?.message && actionableAssignmentErrors.hasOwnProperty(err.response.data.message)) {
        displayErrMessage = actionableAssignmentErrors[err.response.data.message];
        shouldAutoRemove = false;
      }

      const notifications: NotificationItem.PropsWithoutId[] = [
        {
          type: 'error',
          iconType: 'error',
          content: displayErrMessage,
          shouldAutoRemove,
        },
      ];
      addNotifications(notifications)(dispatch);
      setIsSubmitting(false);
    }
  };

  const canSeeAssignmentButton =
    (permissions.includes(NotaryAPI.BSRPermissions.BSRSuperAdmin) ||
      permissions.includes(NotaryAPI.BSRPermissions.BSRAdmin)) &&
    value.roomStatus !== RoomStatus.PAUSED;

  return (
    <LinkButton
      disabled={isSubmitting || !canSeeAssignmentButton}
      label="Assign to me"
      onClick={onAssignClick}
      data-it={`link-assign-me-${value.roomId}`}
    />
  );
};
